import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  HeaderBase,
  HeaderUpperFlexbox,
  HeaderLowerFlexbox,
  MobileLinks,
  CustomIcon,
  SearchCloseIcon,
  SearchBarContainer,
  Search,
  SearchBarBackground,
  SearchOverlay,
  LogoLinkWrapper,
  LoadingBlock,
  LoadingPanel,
  CustomButton,
  LoginButton,
  HeaderLinksWrapper,
  SearchToggle,
  LogoContainer,
  MobileSearchButton,
  AccountButton,
  DropdownCustomOption,
  DropdownCustomContent,
} from "./header.styled";
import { isMobile } from "react-device-detect";
import { HeaderCtAsNodeFragment } from "../../types/graphql";
import HeaderLinks, { useNavHold } from "../header-links/header-links";
import { IStructuredHeaderLinksData } from "../header-links/pure-header-links";
import HeaderCTAs from "../header-ctas/header-ctas";
import { track } from "../../utils/analytics";
import loadable from "@loadable/component";
import * as FA from "../../styles/fontawesome";
import { UserValidationContext } from "../../utils/contexts/user-validation-context";
import { workcenterBaseDomain } from "../../utils/constants";
import { useThemeContext } from "../../utils/contexts/theme-context";
import { LogoDesktop } from "./logos";
import clsx from "clsx";
import { Dropdown } from "@xometry/xometry_loft";

// Code split search for header to optimize common-js bundle
const InstantSearchBox = loadable(() => import("./header-search-box"));

export interface IHeaderProps {
  headerLinksUID: string;
  headerCTAsUID: string;
  disableSearch: boolean;
  showAnnouncementBanner: boolean;
  isDarkMode?: boolean;
  navExperimentEligible?: boolean;
}

export const LoadingSearchSkeletonCard = () => (
  <LoadingPanel>
    <LoadingBlock />
  </LoadingPanel>
);

// The header component reders the logo, drichTexters, links,
// and ctas all in one component. Data for the header
// links and header ctas are sourced within their own
// respective components, but are set as state in this
// component so that they can be passed down to the header
// accordion.
const Header = (props: IHeaderProps) => {
  const noLinks = props.headerLinksUID === "no-links";
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const user = useContext(UserValidationContext);
  const isDarkMode = props.isDarkMode || false;
  const [isDesktopNavOpen, setIsDesktopNavOpen] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const navEscapeHandlerRef = useRef<(() => boolean) | null>(null);
  const navCloseHandlerRef = useRef<(() => void) | null>(null);

  const registerNavEscapeHandler = useCallback(
    (handler: (() => boolean) | null) => {
      navEscapeHandlerRef.current = handler;
    },
    []
  );

  const registerNavCloseHandler = useCallback(
    (handler: (() => void) | null) => {
      navCloseHandlerRef.current = handler;
    },
    []
  );

  const closeMobileNav = useCallback(() => {
    track("Header Nav Mobile Menu Closed");
    setIsMobileNavOpen(false);
  }, []);

  const closeSearch = useCallback(() => {
    track("Close Header Search Box");
    setIsSearchOpen(false);
  }, []);

  // State variables for child component data.
  // Values for these are set by passing the set
  // function to the child.
  const [headerLinksData, setHeaderLinksData] =
    useState<IStructuredHeaderLinksData | null>(null);
  const [headerCTAsData, setHeaderCTAsData] =
    useState<HeaderCtAsNodeFragment | null>(null);

  const { currentTransitionPercent } = useThemeContext();

  // global_nav_v2 hold/variant decision, computed once here so the nav and the
  // search toggle hold and reveal together (single experiment evaluation).
  const { held: isNavHeld, useNewNav } = useNavHold(
    props.navExperimentEligible ?? true
  );

  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const header = document.getElementById("site-header");

    // Handler to call on window resize
    const handleResize = () => {
      setTimeout(() => {
        if (header?.offsetHeight) {
          setHeaderHeight(header?.offsetHeight);
        }
      }, 500);
    };

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isAccountDropdownOpen) {
      setIsSearchOpen(false);
    }
  }, [isAccountDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAccountDropdownOpen(false);
      }
    };

    if (isAccountDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAccountDropdownOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      if (navEscapeHandlerRef.current?.()) {
        event.preventDefault();
        return;
      }

      if (isSearchOpen && !props.disableSearch) {
        closeSearch();
        event.preventDefault();
        return;
      }

      if (isAccountDropdownOpen) {
        setIsAccountDropdownOpen(false);
        event.preventDefault();
        return;
      }

      if (isMobileNavOpen) {
        closeMobileNav();
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [
    closeMobileNav,
    closeSearch,
    isAccountDropdownOpen,
    isMobileNavOpen,
    isSearchOpen,
    props.disableSearch,
  ]);

  return (
    <>
      {/* Preload searchbox script on hover interaction with header */}
      <HeaderBase
        id="site-main-header"
        onMouseOver={() => InstantSearchBox.preload()}
        isDarkMode={isDarkMode}
        transitionPercent={currentTransitionPercent}
        isSearchOpen={isSearchOpen}
        isMobileNavOpen={isMobileNavOpen}
        isDesktopNavOpen={isDesktopNavOpen}
        isAccountDropdownOpen={isAccountDropdownOpen}
      >
        <HeaderUpperFlexbox>
          <LogoContainer>
            <LogoLinkWrapper
              aria-label="Home"
              isDarkMode={isDarkMode}
              transitionPercent={currentTransitionPercent}
              href="/"
            >
              <LogoDesktop />
            </LogoLinkWrapper>
          </LogoContainer>

          {/* CTAs  */}
          <HeaderCTAs
            headerCTAsUID={props.headerCTAsUID || "default-ctas"}
            setHeaderCTAsData={setHeaderCTAsData}
          />

          <div
            className={clsx([
              "relative",
              isDarkMode && currentTransitionPercent > 0
                ? "[&_button[kind='text']]:!text-white [&_button[kind='text']]:!bg-transparent"
                : "",
            ])}
          >
            {user.isReady && user.userData && user.isValid && !user.isExpired && (
              // signed in user
              <div ref={accountDropdownRef} className={clsx(["relative"])}>
                <AccountButton
                  kind="text"
                  scale="small"
                  aria-expanded={isAccountDropdownOpen}
                  aria-haspopup="true"
                  aria-controls="account-dropdown-menu"
                  aria-label="My Account menu"
                  onClick={() => {
                    console.log("clicked");
                    setIsAccountDropdownOpen(!isAccountDropdownOpen);
                  }}
                >
                  {!isMobile && (
                    <CustomIcon
                      css={FA.UserCircle}
                      isDarkMode={isDarkMode}
                      transitionPercent={currentTransitionPercent}
                      aria-hidden="true"
                    />
                  )}
                  My Account
                  <CustomIcon
                    className={clsx([
                      "transition-all",
                      isAccountDropdownOpen ? "rotate-180" : "",
                    ])}
                    css={FA.ChevronDown}
                    isDarkMode={isDarkMode}
                    transitionPercent={currentTransitionPercent}
                    aria-hidden="true"
                  />
                </AccountButton>
                <ul
                  id="account-dropdown-menu"
                  className={clsx([
                    "absolute right-0 top-10 list-none !m-0",
                    "rounded-lg bg-white shadow-lg min-w-50",
                    "border-xom-dark-blue-25 border-1",
                    "transition-all duration-500 ease-in-out",
                    isAccountDropdownOpen
                      ? "translate-y-0 opacity-100"
                      : "-translate-y-200 opacity-0",
                  ])}
                  aria-hidden={!isAccountDropdownOpen}
                >
                  <li
                    className="px-4 py-4 !m-0 hover:bg-xom-dark-blue-5 cursor-pointer rounded-t-lg"
                    onClick={() => {
                      window.location.href = "/quoting/home";
                    }}
                  >
                    Dashboard
                  </li>
                  {user.partner?.id ? (
                    <li
                      className="px-4 py-4 !m-0 hover:bg-xom-dark-blue-5 cursor-pointer"
                      onClick={() => {
                        window.location.href = `https://${workcenterBaseDomain}`;
                      }}
                    >
                      Workcenter
                    </li>
                  ) : (
                    <></>
                  )}
                  <li
                    className="px-4 py-4 !m-0 hover:bg-xom-dark-blue-5 cursor-pointer"
                    onClick={() => {
                      window.location.href = "/quoting/history";
                    }}
                  >
                    Quotes
                  </li>
                  <li
                    className="px-4 py-4 !m-0 hover:bg-xom-dark-blue-5 cursor-pointer"
                    onClick={() => {
                      window.location.href = "/orders";
                    }}
                  >
                    Orders
                  </li>
                  <li
                    className="px-4 py-4 !m-0 hover:bg-xom-dark-blue-5 cursor-pointer"
                    onClick={() => {
                      window.location.href = "/quoting/parts";
                    }}
                  >
                    Parts Library
                  </li>
                  <li
                    className="px-4 py-4 !m-0 hover:bg-xom-dark-blue-5 cursor-pointer"
                    onClick={() => {
                      window.location.href = "/tools";
                    }}
                  >
                    Tools Library
                  </li>
                  <li
                    className="px-4 py-4 !m-0 hover:bg-xom-dark-blue-5 cursor-pointer"
                    onClick={() => {
                      window.location.href = "/user-settings";
                    }}
                  >
                    Account Settings
                  </li>
                  <li
                    className="px-4 py-4 !m-0 hover:bg-xom-dark-blue-5 cursor-pointer border-t-1 border-t-xom-dark-blue-25 rounded-b-lg"
                    onClick={() => {
                      window.location.href = "/logout";
                    }}
                  >
                    Log Out
                  </li>
                </ul>
              </div>
            )}

            {/* Log in, if the user is not logged in or the token expired */}
            {user.isReady && (user.isExpired || !user.isValid) && (
              <LoginButton
                className="relative"
                kind={"text"}
                onClick={() => {
                  window.location.href = "/login";
                }}
                scale="small"
                isDarkMode={isDarkMode}
                transitionPercent={currentTransitionPercent}
              >
                Log In / Register
              </LoginButton>
            )}
          </div>

          {!noLinks && (
            <MobileSearchButton>
              <CustomButton
                kind="text"
                scale="small"
                cssClass="mobile-search-button"
                aria-label={isSearchOpen ? "Close search" : "Open search"}
                aria-expanded={isSearchOpen}
                onClick={() => {
                  if (isSearchOpen) {
                    closeSearch();
                    return;
                  }
                  track("Open Header Search Box");
                  setIsSearchOpen(true);
                }}
              >
                <CustomIcon
                  css={FA.MagnifyingGlassSolid}
                  isDarkMode={isDarkMode}
                  transitionPercent={currentTransitionPercent}
                  aria-hidden="true"
                />
              </CustomButton>
            </MobileSearchButton>
          )}

          {/* Mobile header toggle */}
          {!noLinks && (
            <MobileLinks tabIndex={0}>
              {isMobileNavOpen ? (
                <CustomButton
                  kind={"text"}
                  scale="small"
                  aria-label="Close menu"
                  aria-expanded={isMobileNavOpen}
                  onClick={() => {
                    track("Header Nav Mobile Menu Closed");
                    setIsMobileNavOpen(false);
                  }}
                >
                  <CustomIcon
                    css={FA.XMarkSolid}
                    isDarkMode={false}
                    transitionPercent={currentTransitionPercent}
                    aria-hidden="true"
                  />
                </CustomButton>
              ) : (
                <CustomButton
                  kind={"text"}
                  scale="small"
                  aria-label="Open menu"
                  aria-expanded={isMobileNavOpen}
                  onClick={() => {
                    track("Header Nav Mobile Menu Opened");
                    setIsMobileNavOpen(true);
                  }}
                >
                  <CustomIcon
                    css={FA.BarsSolid}
                    isDarkMode={isDarkMode}
                    transitionPercent={currentTransitionPercent}
                    aria-hidden="true"
                  />
                </CustomButton>
              )}
            </MobileLinks>
          )}
        </HeaderUpperFlexbox>

        {!noLinks && (
          <HeaderLowerFlexbox>
            <HeaderLinksWrapper isMobileNavOpen={isMobileNavOpen}>
              <HeaderLinks
                headerLinksUID={props.headerLinksUID || "default-links"}
                held={isNavHeld}
                useNewNav={useNewNav}
                setHeaderLinksData={setHeaderLinksData}
                isHeaderLinksDataSet={!!headerLinksData}
                isOpen={isMobileNavOpen}
                showAnnouncementBanner={props.showAnnouncementBanner}
                isDarkMode={isDarkMode}
                setIsDesktopNavOpen={(isOpen) => setIsDesktopNavOpen(isOpen)}
                registerEscapeHandler={registerNavEscapeHandler}
                registerNavCloseHandler={registerNavCloseHandler}
                onRequestCloseMobileNav={closeMobileNav}
              />
              <SearchToggle
                $held={isNavHeld}
                role="button"
                tabIndex={0}
                aria-label={isSearchOpen ? "Close search" : "Open search"}
                aria-expanded={isSearchOpen}
                onClick={() => {
                  if (isSearchOpen) {
                    closeSearch();
                    return;
                  }
                  track("Open Header Search Box");
                  navCloseHandlerRef.current?.();
                  setIsSearchOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (isSearchOpen) {
                      closeSearch();
                      return;
                    }
                    track("Open Header Search Box");
                    navCloseHandlerRef.current?.();
                    setIsSearchOpen(true);
                  }
                }}
              >
                <CustomIcon
                  css={FA.MagnifyingGlassSolid}
                  isDarkMode={isDarkMode}
                  transitionPercent={currentTransitionPercent}
                  aria-hidden="true"
                />
              </SearchToggle>
            </HeaderLinksWrapper>
          </HeaderLowerFlexbox>
        )}
      </HeaderBase>
      <Search
        isMobileNavOpen={isSearchOpen && !props.disableSearch}
        showAnnouncementBanner={props.showAnnouncementBanner}
        offsetTop={headerHeight}
        role="search"
        aria-label="Site search"
      >
        <SearchBarBackground
          isMobileNavOpen={isSearchOpen && !props.disableSearch}
          showAnnouncementBanner={props.showAnnouncementBanner}
        >
          <SearchBarContainer>
            <InstantSearchBox fallback={<LoadingSearchSkeletonCard />} />
            <SearchCloseIcon
              css={FA.XMarkRegular}
              role="button"
              tabIndex={0}
              aria-label="Close search"
              onClick={closeSearch}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  closeSearch();
                }
              }}
            />
          </SearchBarContainer>
        </SearchBarBackground>
      </Search>
      <SearchOverlay
        isMobileNavOpen={isSearchOpen && !props.disableSearch}
        showAnnouncementBanner={props.showAnnouncementBanner}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default Header;
